---
title: "Glossary"
slug: glossary
description: "Definitions of key terms in hey circle: GRAI, location, package status, inventory, and contract."
keywords:
  - glossary
  - GRAI
  - location
  - package status
  - inventory
  - contract
  - terms
---

## Active

Package status. A package with the status **Active** is in normal operating condition and can be used for shipments. The package has sufficient remaining lifetime (shipments or days). See [Reading the Analytics Dashboard](/app/help?article=viewing-analytics) for a status breakdown.

## Contract

The contractual basis for a group of hey circle packages. A contract defines the terms of use, for example the maximum number of shipments per package or the contract duration. Packages are assigned to a contract, and their status (Near EOL, End of Life) is calculated relative to the contract terms.

## Defect

Package status. A package with the status **Defect** has been flagged via a defect report. It is unavailable for normal operations until the issue is resolved by the hey circle support team. See [Reporting a Defect](/app/help?article=reporting-a-defect).

## End of Life (EOL)

Package status. A package has reached the end of its contractual service life — either the maximum number of shipments or the agreed duration. The package is removed from the active inventory. Abbreviation: EOL.

## External destination

A recipient outside your own internal locations, for example a customer, a partner, or a logistics provider. External destinations can be created by Company Admins and Managers under **Settings → Recipients**. Packages sent to an external destination leave your internal inventory until they are received back at an internal location. See [Initial Setup](/app/help?article=initial-setup).

## GRAI

Global Returnable Asset Identifier. A unique 34-character identifier printed on every hey circle package as a QR code and in plain text. The GRAI permanently identifies a single physical package and enables complete traceability across all locations and shipments.

## Internal location

A location that belongs to your company and at which packages can be scanned. Only internal locations appear in the scan dropdown. At least one internal location must be created before users can begin scanning. See [Initial Setup](/app/help?article=initial-setup).

## Inventory

The current stock of all packages in your company. Inventory is created automatically: every scan of a package creates an entry or updates the existing entry. There is no manual step to create inventory. The current stock is visible under **Inventory Health** in the Analytics Dashboard. See [Reading the Analytics Dashboard](/app/help?article=viewing-analytics).

## Location

General term for both internal locations and external destinations in hey circle. In the narrower sense, "location" refers to an internal location (a site belonging to your company that has scanning rights). External destinations are technically separate entities. See **Internal location** and **External destination**.

## Near EOL

Package status. A package is approaching the end of its service life — either the maximum number of shipments or the end of the contract duration. The package is still usable but should be replaced soon. Displayed as **Near EOL** in the inventory overview.

## Package status

hey circle tracks two independent status dimensions for every package.

**Condition** — the physical state of a package, shown in the inventory overview and in **Inventory Health**:

| Condition | Meaning |
|---|---|
| **Active** | Normal operation, package can be shipped |
| **Defect** | Damage report filed, awaiting support review |
| **Near EOL** | Approaching end of service life |
| **End of Life** | Service life expired, removed from inventory |

**Circulation status** — where a package currently is in its shipment cycle: **Available** (in stock at an internal location), **In Transit** (sent out, not yet received back), and **Completed** (invoice settled or cycle closed). See [Package Lifecycle](/app/help?article=package-lifecycle).

## Shipment

A single send operation: a package is sent from one location to an internal or external destination. Each shipment creates an entry in the package's activity history. See [Sending a Package](/app/help?article=sending-a-package).
